import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import store from 'Store/index';
import type { RootState } from 'Store/index';
import {
	Addon,
	FeatureName,
	ProductName,
	isAvailable as isAvailableThunk,
	hasSoftDeployAccess as hasSoftDeployAccessThunk,
	isAvailableProduct as isAvailableProductThunk,
	isAvailableAddon as isAvailableAddonThunk
} from 'Store/actions/FeatureHelperActions';

/*
	Standalone, synchronous replacement for Tools.FeatureHelper (WEB-7606).

	This is a 1:1 behavioural match for the old Angular FeatureHelper factory, which dispatched these
	same read-only thunks against the Redux store. Use it anywhere Tools.FeatureHelper was used:
	function components, class components, helpers, attributes, filter configs, store thunks, etc.

	A synchronous read is behaviour-preserving because it matches what Tools.FeatureHelper did — not
	because flags never change. Soft-deploy flags can flip mid-session: the push handler in
	upsales/common/resources/pushNotifications.js writes unreleasedFeatures then calls setAccountSelf.
	Callers that should re-render on a flip want the hooks in App/components/hooks instead.
*/

// store.dispatch is typed as the base Redux dispatch; the thunk middleware makes it return the
// thunk's value. Mirror the useAppDispatch pattern used elsewhere for the correct return types.
// Accessed lazily (inside each function) so importing this module never touches the store at load
// time — importing it must be safe before the store is created.
const dispatch = () => store.dispatch as ThunkDispatch<RootState, unknown, AnyAction>;

export const isAvailable = (feature: FeatureName, searchAccount?: boolean): boolean =>
	dispatch()(isAvailableThunk(feature, searchAccount));

export const hasSoftDeployAccess = (feature: string): boolean => dispatch()(hasSoftDeployAccessThunk(feature));

export const isAvailableProduct = (product: ProductName): boolean => dispatch()(isAvailableProductThunk(product));

export const isAvailableAddon = (addon: Addon): boolean => dispatch()(isAvailableAddonThunk(addon));

export { Addon, Feature, Product } from 'Store/actions/FeatureHelperActions';
export type { FeatureName, ProductName } from 'Store/actions/FeatureHelperActions';
